import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, fmtMoney, fmtQty } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common";
import SystemHealth from "../components/SystemHealth";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, FileText, Receipt, PackageCheck, Wrench, ShoppingCart, Scale, ArrowRightLeft, AlertOctagon, ChevronRight, Truck, MessageSquareWarning } from "lucide-react";
import { DeliveriesPanel } from "../components/DeliveriesPanel";
import { OnboardingBanner } from "../components/OnboardingBanner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const Kpi = ({ label, value, testid, alert }) => (
  <div className={`border bg-card p-4 ${alert ? "border-primary" : ""}`} data-testid={testid}>
    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
    <div className={`text-2xl font-black tracking-tight mt-1 tabular-nums ${alert ? "text-primary" : ""}`}>{value}</div>
  </div>
);

const DESK_ITEMS = [
  { key: "requisitions", label: "Requisiciones por aprobar", icon: FileText, tone: "amber", route: "/requisitions?status=pending" },
  { key: "vales", label: "Vales por autorizar", icon: Receipt, tone: "amber", route: "/vales?status=pendiente" },
  { key: "vales_supply", label: "Vales por surtir", icon: PackageCheck, tone: "blue", route: "/vales?status=autorizado" },
  { key: "work_orders", label: "OTs asignadas a mí", icon: Wrench, tone: "primary", route: "/mnt/work-orders" },
  { key: "purchase_orders", label: "OCs abiertas", icon: ShoppingCart, tone: "blue", route: "/purchase-orders?status=open" },
  { key: "invoices", label: "Facturas por conciliar", icon: Scale, tone: "primary", route: "/match" },
  { key: "avisos", label: "Avisos abiertos", icon: AlertOctagon, tone: "rose", route: "/mnt/notifications" },
  { key: "internal_requisitions", label: "Requisiciones internas", icon: ArrowRightLeft, tone: "blue", route: "/internal-requisitions" },
  { key: "po_overdue", label: "OCs con entrega vencida", icon: Truck, tone: "rose", route: "/purchase-orders?overdue=1" },
  { key: "po_responses", label: "Respuestas de proveedor", icon: MessageSquareWarning, tone: "amber", route: "/purchase-orders?responses=1" },
];

const TONE_CLS = {
  amber: "border-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-100",
  blue: "border-blue-500 bg-blue-50 text-blue-900 hover:bg-blue-100",
  rose: "border-rose-500 bg-rose-50 text-rose-900 hover:bg-rose-100",
  primary: "border-primary bg-primary/5 text-primary hover:bg-primary/10",
};

function MyDesk() {
  const navigate = useNavigate();
  const [inbox, setInbox] = useState(null);
  const load = useCallback(() => {
    api.get("/my/inbox").then((r) => setInbox(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  if (!inbox) return null;
  const totals = inbox.totals || {};
  const visible = DESK_ITEMS.filter((it) => (totals[it.key] || 0) > 0);
  const totalPending = (totals.total || 0);
  return (
    <>
    <div className="border bg-card p-4 mb-6" data-testid="my-desk-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5">
          <FileText size={12} /> Mi Escritorio · pendientes por atender
        </div>
        <div className="text-[10px] font-bold text-muted-foreground">
          <span className="text-primary font-black tabular-nums">{totalPending}</span> total
        </div>
      </div>
      {visible.length === 0 ? (
        <div className="text-xs text-muted-foreground italic py-2" data-testid="my-desk-empty">
          🎉 Nada pendiente por atender — ¡al día!
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {visible.map((it) => {
            const count = totals[it.key] || 0;
            const cls = TONE_CLS[it.tone] || TONE_CLS.primary;
            return (
              <button key={it.key}
                      onClick={() => navigate(it.route)}
                      className={`text-left border-l-4 p-3 transition-colors ${cls}`}
                      data-testid={`my-desk-${it.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <it.icon size={16} />
                  <span className="text-2xl font-black tabular-nums leading-none">{count}</span>
                </div>
                <div className="text-[10px] font-bold uppercase tracking-widest mt-2 leading-tight">{it.label}</div>
                <div className="flex items-center gap-1 text-[9px] mt-1 opacity-70">
                  Ver <ChevronRight size={9} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
    <DeliveriesPanel inbox={inbox} onChanged={load} />
    </>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(() => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const createReq = async () => {
    try {
      const { data: req } = await api.post("/stock-alerts/requisition",
        { item_numbers: data.stock_alerts.map((a) => a.item_number) });
      toast.success(`${t("al.reqCreated")}: ${req.number}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="text-xs text-muted-foreground">{t("common.loading")}</div>;
  const k = data.kpis;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={t("dash.title")} />
      {can("admin", "buyer") && <OnboardingBanner />}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Kpi label={t("dash.pendingReqs")} value={k.pending_requisitions} testid="kpi-pending-reqs" />
        <Kpi label={t("dash.openPos")} value={k.open_pos} testid="kpi-open-pos" />
        <Kpi label={t("dash.invValue")} value={fmtMoney(k.inventory_value)} testid="kpi-inventory-value" />
        <Kpi label={t("dash.items")} value={k.total_items} testid="kpi-items" />
        <Kpi label={t("dash.pendingInvoices")} value={k.pending_invoices} testid="kpi-pending-invoices" />
        <Kpi label={t("al.kpi")} value={k.low_stock} testid="kpi-low-stock" alert={k.low_stock > 0} />
      </div>

      <MyDesk />

      {can("admin", "buyer") && <SystemHealth />}
      <div className="border bg-card p-4 mb-6" data-testid="stock-alerts-section">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5">
            <AlertTriangle size={12} className={k.low_stock > 0 ? "text-primary" : ""} /> {t("al.title")}
          </div>
          {k.low_stock > 0 && can("admin", "buyer", "warehouse") && (
            <Button size="sm" onClick={createReq} className="rounded-none text-xs font-bold" data-testid="alerts-create-req-button">
              {t("al.createReq")}
            </Button>
          )}
        </div>
        {data.stock_alerts.length === 0 ? (
          <div className="text-xs text-muted-foreground">{t("al.none")}</div>
        ) : (
          <div className="tbl-wrap" data-testid="stock-alerts-table">
            <table className="tbl">
              <thead><tr>
                <th>{t("common.item")}</th><th>{t("common.description")}</th>
                <th className="num">{t("al.minStock")}</th><th className="num">{t("al.onHand")}</th>
                <th className="num">{t("al.shortage")}</th><th className="num">{t("al.estTotal")}</th>
              </tr></thead>
              <tbody>
                {data.stock_alerts.map((a) => (
                  <tr key={a.item_number}>
                    <td className="font-bold">{a.item_number}</td>
                    <td>{a.description}</td>
                    <td className="num">{fmtQty(a.min_stock)}</td>
                    <td className="num font-bold text-destructive">{fmtQty(a.on_hand)}</td>
                    <td className="num font-bold">{fmtQty(a.shortage)} {a.uom}</td>
                    <td className="num">{fmtMoney(a.est_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="border bg-card p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">{t("dash.poByStatus")}</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.po_status.map((s) => ({ name: t(`st.${s.status}`), count: s.count }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
              <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ fontSize: 11, borderRadius: 0 }} />
              <Bar dataKey="count" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border bg-card p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">{t("dash.invByWh")}</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.inventory_by_warehouse} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="warehouse" tick={{ fontSize: 10 }} width={60} />
              <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ fontSize: 11, borderRadius: 0 }} />
              <Bar dataKey="value" fill="hsl(var(--chart-2))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("dash.invByFamily")}</div>
        <div className="tbl-wrap" data-testid="inventory-by-family-table">
          <table className="tbl">
            <thead><tr>
              <th>{t("dash.familyCode")}</th>
              <th>{t("dash.familyName")}</th>
              <th className="num">{t("dash.itemsCount")}</th>
              <th className="num">{t("dash.qtyOnHand")}</th>
              <th className="num">{t("dash.currentValue")}</th>
            </tr></thead>
            <tbody>
              {(data.inventory_by_family || []).length === 0 && (
                <tr><td colSpan={5} className="text-xs text-muted-foreground text-center py-3">{t("common.noData")}</td></tr>
              )}
              {(data.inventory_by_family || []).map((f) => (
                <tr key={f.code + f.name} data-testid={`fam-row-${f.code}`}>
                  <td className="font-bold">{f.code}</td>
                  <td>{f.name}</td>
                  <td className="num">{f.item_count}</td>
                  <td className="num">{fmtQty(f.qty_on_hand)}</td>
                  <td className="num font-bold text-primary tabular-nums">{fmtMoney(f.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
